import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {ExtractProps} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChannelModel} from '../../../libs/shared/core/src/models/api/channelModel';
import {ConversationActionContextsEnum} from '../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {MessageModel} from '../../../libs/shared/core/src/models/api/messageModel';
import {MessagePanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {stretch} from '../../../libs/shared/ui/src/styles/mixins';
import {ExpandedDarkThemeProvider} from '../../../libs/shared/ui/src/theme/expandedDarkThemeProvider';
import {Frame} from '../common/frames/frame';
import {MessageViewerContainer} from '../inboxes/viewer/activity/message/messageViewerContainer';
import {useMessagePrintHandler} from './popupPrintHelpers';

/*
 * Props.
 */

interface PopupMessageViewerProps extends ExtractProps<typeof MessageViewerContainer> {
  panelId: MessagePanelId;
  shouldForcePopout?: boolean;
  namespace: string;
  conversationId: number;
  conversationActionContext: ConversationActionContextsEnum | undefined;
  isSpam: boolean;
  message: MessageModel | undefined;
  messageUid: string;
  onRequestClose: () => void;
  replyChannel?: ChannelModel;
  initialHeight?: number;
}

/*
 * Style.
 */

const StyledViewerDiv = styled.div`
  flex: 1;
  min-height: 0;
  flex-direction: column;
  ${stretch()}
`;

/*
 * Component.
 */

export const PopupMessageViewer: FC<PopupMessageViewerProps> = (props) => {
  const [shouldPopoutFrame, setShouldPopoutFrame] = useState(Boolean(props.shouldForcePopout));
  const onRequestPrint = useMessagePrintHandler(props.message);

  return (
    <ExpandedDarkThemeProvider>
      <Frame
        minWidth="540px"
        width={740}
        height={props.initialHeight || 600}
        shouldForcePopout={shouldPopoutFrame}
        onRequestClose={props.onRequestClose}
        onRequestPrint={onRequestPrint}
        startHeight={undefined}
        interactionId="popup-viewer-message"
        onOpenPopout={() => setShouldPopoutFrame(true)}
      >
        <StyledViewerDiv>
          <MessageViewerContainer
            panelId={props.panelId}
            cellsContext={props.cellsContext}
            namespace={props.namespace}
            conversationId={props.conversationId}
            conversationActionContext={props.conversationActionContext}
            isSpam={props.isSpam}
            messageUid={props.messageUid}
            activityId={props.activityId}
            state={props.state}
            previousMessageUid={props.previousMessageUid}
            replyChannel={props.replyChannel}
            timelineVariation={props.timelineVariation}
          />
        </StyledViewerDiv>
      </Frame>
    </ExpandedDarkThemeProvider>
  );
};
